/**
 * WP-3 — `add_to_cart` commerce action handler.
 *
 * The storefront-funnel lib never calls carts-ts directly. This handler resolves
 * the authored product reference (folded from the triggering event's params by
 * the H4 `paramsFrom` step) and forwards to the app-injected `onAddToCart`
 * callback, which runs the SKU→UUID resolution and mutates the cart ONLY through
 * the server-validated BFF cart path (`useFunnelAddToCart` → `useAddToCart` →
 * storefront-bff `/v1/storefront/carts` → carts-ts, request shaped with
 * `@trafilea/tss-lib-carts-contracts` `AddItemRequest`/`CreateCartItem`).
 *
 * No cart callback wired (tests, non-commerce host) → loud no-op, never silent.
 */

import { CartItemSubscriptionInputSchema } from '@trafilea/tss-lib-carts-contracts';
import type {
  ActionExecutionContext,
  ActionExecutionResult,
  ActionHandler,
  FunnelAddToCartItem,
} from './types';
import { semanticPortContext } from './ports';

function readString(config: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = config[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
}

function readQuantity(config: Record<string, unknown>): number {
  const raw = config.quantity ?? config.qty;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

export const addToCartHandler: ActionHandler = {
  type: 'add_to_cart',
  execute(ctx: ActionExecutionContext): ActionExecutionResult | Promise<ActionExecutionResult> {
    const config = ctx.config as unknown as Record<string, unknown>;
    // Accept both snake_case (authored) and camelCase (paramsFrom mappings).
    const productRef = readString(config, 'productId', 'product_id', 'sku');
    const variantRef = readString(config, 'variantId', 'variant_id') ?? '';
    const quantity = readQuantity(config);

    if (!productRef) {
      ctx.logger.warn('add_to_cart skipped: no product reference in action config', {
        stepId: ctx.stepId,
        actionId: ctx.action.id,
      });
      return {
        status: 'failed',
        reason: 'missing_product_reference',
      };
    }

    // T26: validate authored subscription extras against the CANONICAL cart
    // contract (never invent a parallel shape). Invalid input is loud + omitted
    // so a typo can't block the purchase flow silently.
    let item: FunnelAddToCartItem = {
      semanticResultOwner: 'dispatcher',
    };
    const rawSubscription = config.subscription;
    if (rawSubscription !== undefined && rawSubscription !== null) {
      const parsed = CartItemSubscriptionInputSchema.safeParse(rawSubscription);
      if (parsed.success) {
        item = {
          ...item,
          subscription: parsed.data,
        };
      } else {
        ctx.logger.warn(
          'add_to_cart: authored subscription failed carts-contracts validation — adding base item without it',
          {
            stepId: ctx.stepId,
            actionId: ctx.action.id,
            issues: parsed.error.issues.map((issue) => issue.message).slice(0, 3),
          }
        );
      }
    }

    const semanticPort = ctx.semanticPorts?.addToCart;
    if (!semanticPort && !ctx.onAddToCart) {
      // Recognized action, but the host did not wire a cart path. Loud, not silent.
      ctx.logger.warn(
        'add_to_cart unsupported: no server-validated cart callback wired in this host',
        {
          stepId: ctx.stepId,
          actionId: ctx.action.id,
          productRef,
        }
      );
      return {
        status: 'unsupported',
        unsupported: true,
        reason: 'missing_add_to_cart_capability',
      };
    }

    const invocation = semanticPort
      ? semanticPort(
          { productId: productRef, variantId: variantRef, quantity, item },
          semanticPortContext(ctx)
        )
      : ctx.onAddToCart!(productRef, variantRef, quantity, item);
    return Promise.resolve(invocation)
      .then(
        (result) => {
          const hostResultParams =
            result && typeof result === 'object' && 'resultParams' in result
              ? result.resultParams
              : undefined;
          return {
            status: 'succeeded',
            resultParams: {
              productId: productRef,
              ...(variantRef ? { variantId: variantRef } : {}),
              quantity,
              ...(hostResultParams ?? {}),
            },
          } satisfies ActionExecutionResult;
        }
      )
      .catch((error: unknown): ActionExecutionResult => {
        ctx.logger.error(
          'add_to_cart failed at the BFF cart path',
          error instanceof Error ? error : undefined,
          { stepId: ctx.stepId, actionId: ctx.action.id, productRef }
        );
        return {
          status: 'failed',
          reason: 'add_to_cart_failed',
        };
      });
  },
};
